/**
 * ProductScannerEmbedded - Wbudowana wersja skanera produktów
 * Używana w formularzu tworzenia produktu, obsługuje wiele zdjęć
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Stack,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { DetectionSuggestion, DetectionResponse } from './types';
import { aggregateDetectionResults, AggregatedSuggestion } from './utils';
import { API_BASE_URL } from '../../utils/apiConfig';
import { useTranslation } from 'react-i18next';

interface ProductScannerEmbeddedProps {
  onSuggestionsChange?: (aggregated: AggregatedSuggestion | null) => void;
}

interface PhotoWithResults {
  id: string;
  image: string; // data URL
  loading: boolean;
  error: string | null;
  suggestions: DetectionSuggestion[];
  detectionResult: DetectionResponse | null;
}

const ProductScannerEmbedded: React.FC<ProductScannerEmbeddedProps> = ({
  onSuggestionsChange,
}) => {
  const { t } = useTranslation();
  const [photos, setPhotos] = useState<PhotoWithResults[]>([]);
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [analyzingPhotoId, setAnalyzingPhotoId] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // API_BASE_URL is imported at the top

  // Initialize camera when dialog opens
  useEffect(() => {
    if (cameraDialogOpen) {
      const initCamera = async () => {
        try {
          const mediaStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'environment' }
          });
          setStream(mediaStream);
          if (videoRef.current) {
            videoRef.current.srcObject = mediaStream;
          }
        } catch (err: any) {
          console.error('Camera access error:', err);
        }
      };

      initCamera();

      return () => {
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cleanup uses stream from closure
  }, [cameraDialogOpen]);

  // Notify parent when suggestions change
  useEffect(() => {
    if (onSuggestionsChange) {
      const allSuggestions = photos
        .filter(p => p.suggestions.length > 0)
        .map(p => p.suggestions);

      const aggregated = aggregateDetectionResults(allSuggestions);
      onSuggestionsChange(aggregated);
    }
  }, [photos, onSuggestionsChange]);

  // Capture photo from video stream
  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(video, 0, 0);
      const imageDataUrl = canvas.toDataURL('image/jpeg', 0.9);
      setCapturedImage(imageDataUrl);
    }
  }, []);

  // Add photo to list
  const handleAddPhoto = useCallback(() => {
    if (capturedImage) {
      const newPhoto: PhotoWithResults = {
        id: `photo-${Date.now()}`,
        image: capturedImage,
        loading: false,
        error: null,
        suggestions: [],
        detectionResult: null,
      };

      setPhotos(prev => [...prev, newPhoto]);
      setCapturedImage(null);
      setCameraDialogOpen(false);

      // Stop camera stream
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
        setStream(null);
      }
    }
  }, [capturedImage, stream]);

  // Handle file upload
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.type.startsWith('image/')) {
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const imageDataUrl = e.target?.result as string;
      const newPhoto: PhotoWithResults = {
        id: `photo-${Date.now()}`,
        image: imageDataUrl,
        loading: false,
        error: null,
        suggestions: [],
        detectionResult: null,
      };

      setPhotos(prev => [...prev, newPhoto]);
    };
    reader.readAsDataURL(selectedFile);
  }, []);

  // Analyze single photo
  const handleAnalyzePhoto = async (photoId: string) => {
    const photo = photos.find(p => p.id === photoId);
    if (!photo) return;

    setAnalyzingPhotoId(photoId);
    setPhotos(prev => prev.map(p =>
      p.id === photoId ? { ...p, loading: true, error: null } : p
    ));

    try {
      // Convert data URL to blob
      const response = await fetch(photo.image);
      const blob = await response.blob();

      const formData = new FormData();
      formData.append('image', blob, 'photo.jpg');

      const apiResponse = await axios.post<DetectionResponse>(
        `${API_BASE_URL}/products/detect_from_image/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setPhotos(prev => prev.map(p =>
        p.id === photoId
          ? {
            ...p,
            loading: false,
            suggestions: apiResponse.data.suggestions,
            detectionResult: apiResponse.data,
          }
          : p
      ));

    } catch (err: any) {
      console.error('Detection error:', err);
      const errorMessage = axios.isAxiosError(err)
        ? err.response?.data?.error || err.message
        : 'Wystąpił błąd podczas analizy';

      setPhotos(prev => prev.map(p =>
        p.id === photoId
          ? { ...p, loading: false, error: errorMessage }
          : p
      ));
    } finally {
      setAnalyzingPhotoId(null);
    }
  };

  // Remove photo
  const handleRemovePhoto = useCallback((photoId: string) => {
    setPhotos(prev => prev.filter(p => p.id !== photoId));
  }, []);

  // Close camera dialog
  const handleCloseCameraDialog = useCallback(() => {
    setCameraDialogOpen(false);
    setCapturedImage(null);
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  }, [stream]);

  return (
    <Box>
      {/* Add photo button */}
      <Button
        variant="outlined"
        startIcon={<CameraIcon sx={{ color: 'white' }} />}
        onClick={() => setCameraDialogOpen(true)}
        fullWidth
        sx={{
          mb: 2,
          color: 'white',
          borderColor: 'white',
          '&:hover': {
            borderColor: 'rgba(255, 255, 255, 0.8)',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        {t("products.form.addPhoto")}
      </Button>

      {/* Photos list */}
      {photos.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t("products.form.photos")} ({photos.length})
          </Typography>
          <Stack spacing={2} direction="row" flexWrap="wrap" useFlexGap>
            {photos.map((photo) => (
              <Box key={photo.id} sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33.333% - 11px)' } }}>
                <Card>
                  <CardContent>
                    <Box sx={{ position: 'relative', mb: 1 }}>
                      <img
                        src={photo.image}
                        alt="Product"
                        style={{
                          width: '100%',
                          height: 150,
                          objectFit: 'cover',
                          borderRadius: 4,
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleRemovePhoto(photo.id)}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          bgcolor: 'rgba(0,0,0,0.5)',
                          color: 'white',
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {photo.error && (
                      <Alert severity="error" sx={{ mb: 1, fontSize: '0.75rem' }}>
                        {photo.error}
                      </Alert>
                    )}

                    {photo.suggestions.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" display="block">
                          {t("products.form.detected")}: {photo.suggestions.length} {photo.suggestions.length === 1 ? t("products.form.product") : t("products.form.products")}
                        </Typography>
                        {photo.detectionResult?.requires_manual_review && (
                          <Chip
                            label={t("products.form.requiresVerification")}
                            size="small"
                            color="warning"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </Box>
                    )}

                    <Button
                      size="small"
                      variant="contained"
                      fullWidth
                      onClick={() => handleAnalyzePhoto(photo.id)}
                      disabled={photo.loading || analyzingPhotoId === photo.id}
                      startIcon={photo.loading ? <CircularProgress size={16} /> : <CameraIcon />}
                      sx={{ mt: 1 }}
                    >
                      {photo.loading ? t("products.form.analyzing") : photo.suggestions.length > 0 ? t("products.form.analyzeAgain") : t("products.form.analyze")}
                    </Button>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* Camera dialog */}
      <Dialog
        open={cameraDialogOpen}
        onClose={handleCloseCameraDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1e1e1e',
            color: 'white',
          },
        }}
      >
        <DialogTitle sx={{ color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ color: 'white' }}>
              {t("products.form.takeProductPhoto")}
            </Typography>
            <IconButton onClick={handleCloseCameraDialog} sx={{ color: 'white' }}>
              <CancelIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ color: 'white' }}>
          {!capturedImage ? (
            <Box>
              <Paper
                sx={{
                  p: 2,
                  textAlign: 'center',
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                }}
              >
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{
                    width: '100%',
                    maxHeight: 400,
                    borderRadius: 8,
                  }}
                />
                <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    startIcon={<CameraIcon />}
                    onClick={capturePhoto}
                    disabled={!stream}
                  >
                    {t("products.form.takePhoto")}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => fileInputRef.current?.click()}
                    sx={{ color: 'white', borderColor: '#474747ff' }}
                  >
                    {t("products.form.chooseFile")}
                  </Button>
                </Box>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </Paper>
            </Box>
          ) : (
            <Box>
              <img
                src={capturedImage}
                alt="Captured"
                style={{
                  width: '100%',
                  maxHeight: 400,
                  borderRadius: 8,
                  marginBottom: 16,
                }}
              />
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={<CheckIcon />}
                  onClick={handleAddPhoto}
                >
                  {t("products.form.addPhoto")}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => setCapturedImage(null)}
                  sx={{ color: 'white', borderColor: '#474747ff' }}
                >
                  {t("products.form.retake")}
                </Button>
              </Box>
            </Box>
          )}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ProductScannerEmbedded;

